import { useCallback } from "react";
import type {
  DashboardBlockModelT,
  DashboardImportT,
  DashboardModelT,
} from "../../schema/dashboard";
import {
  dataUrlToFile,
  isEmbeddedImageDataUrl,
  isEmbeddedImageDataUrlCandidate,
} from "../../../../../lib/imageDataUrl";
import { useDashboardEditorContext } from "../context/editor";
import {
  useCreateRemoteDashboardCallback,
  useUpdateRemoteDashboardCallback,
} from "../api/dashboard";
import { uniqBy } from "diagnostics-lib/array";
import { usePost } from "../../../../../api/useCall";

type ImportDashboardResult = {
  result: Awaited<ReturnType<ReturnType<typeof useCreateRemoteDashboardCallback>>> | null;
  hasWarnings: boolean;
};

export const useDashboardImportCallback = (
  projectId: string,
  userId: string,
  published?: boolean,
  dashboardId?: string,
) => {
  const editor = useDashboardEditorContext();
  const createRemoteDashboard = useCreateRemoteDashboardCallback();
  const updateRemoteDashboard = useUpdateRemoteDashboardCallback();
  const upload = usePost("/file", false);

  return useCallback(
    async (dashboard: DashboardImportT) => {
      const { blocks: dashboardBlocks, ...dashboardProps } = dashboard;
      let hasWarnings = false;
      const importedBlocks = await Promise.all(
        dashboardBlocks.map(async (block, index) => {
          if ((block.kind !== "DIAGRAM" && block.kind !== "IMAGE") || !block.config.url) {
            return block;
          }

          if (
            isEmbeddedImageDataUrlCandidate(block.config.url) &&
            !isEmbeddedImageDataUrl(block.config.url)
          ) {
            hasWarnings = true;
            return {
              ...block,
              config: {
                ...block.config,
                url: "",
              },
            };
          }

          if (!isEmbeddedImageDataUrl(block.config.url)) {
            return block;
          }

          let file: File | null = null;
          try {
            file = dataUrlToFile(
              block.config.url,
              `dashboard-${block.kind.toLowerCase()}-${index + 1}`,
            );
          } catch {
            hasWarnings = true;
          }

          if (!file) {
            return {
              ...block,
              config: {
                ...block.config,
                url: "",
              },
            };
          }

          const body = new FormData();
          body.append("path", `${projectId}/dashboard`);
          body.append("file", file);

          const result =
            await // @ts-ignore FormData upload is supported by the backend route typing.
            upload({ body }).catch(() => {
              hasWarnings = true;
              return null;
            });
          if (!result) {
            return {
              ...block,
              config: {
                ...block.config,
                url: "",
              },
            };
          }

          return {
            ...block,
            config: {
              ...block.config,
              url: result.body.path,
            },
          };
        }),
      );
      const nextDashboardId = dashboardId ?? (globalThis.crypto.randomUUID() as string);
      const nextBlocksIds = importedBlocks.map(() => globalThis.crypto.randomUUID() as string);

      const nextDashboard: DashboardModelT = {
        ...dashboardProps,
        id: nextDashboardId,
        createdAt: Date.now(),
        blocks: nextBlocksIds,
        projectId,
        createdByUserId: userId,
        createdByUserName: userId,
        updatedByUserName: userId,
        updatedAt: Date.now(),
        published: published ?? false,
        readonly: false,
      };

      // dedup positions
      const nextBlocks: DashboardBlockModelT[] = uniqBy(
        importedBlocks
          .map((block, i) => ({
            ...block,
            id: nextBlocksIds[i],
            timestamp: Date.now(),
            dashboardId: nextDashboardId,
            position: block.position || [1, i + 1, 1, 1],
          }))
          .filter((b): b is DashboardBlockModelT => Boolean(b)),
        block => block.position.join("-"),
      );

      if (dashboardId && editor) {
        const result = await updateRemoteDashboard(nextDashboard, nextBlocks).then(async result => {
          if (!result) return null;
          await Promise.all([
            editor.updateDashboard(result.dashboard),
            editor.updateManyBlocks(result.blocks),
          ]);
          return result;
        });
        return { result, hasWarnings } satisfies ImportDashboardResult;
      }

      const result = await createRemoteDashboard(nextDashboard, nextBlocks);
      return { result, hasWarnings } satisfies ImportDashboardResult;
    },
    [
      createRemoteDashboard,
      dashboardId,
      editor,
      projectId,
      published,
      upload,
      updateRemoteDashboard,
      userId,
    ],
  );
};
